/**
 * ListPageHeader
 *
 * Reusable header bar for list pages with filter toggle, create, and export buttons.
 * Use this for standalone list pages (not inside SubMenu tabs).
 *
 * For SubMenu pages, import `actionButtonSx` and `filterToggleSx` directly
 * to build your own headerActions in the SubMenu `actions` slot.
 *
 * Usage:
 *   <ListPageHeader
 *     filtersExpanded={filtersExpanded}
 *     onToggleFilters={() => setFiltersExpanded(!filtersExpanded)}
 *     showCreate
 *     onCreateClick={() => navigate('/resource/create')}
 *     showExport
 *     onExportClick={handleExport}
 *   />
 */

import React from 'react';
import { Box, Button, alpha, useTheme, type SxProps, type Theme } from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

/**
 * Shared sx for solid action buttons (Create, Export, etc.)
 *
 * REUSABLE: Import this in pages with SubMenu to style action buttons consistently.
 */
export const actionButtonSx: SxProps<Theme> = {
  backgroundColor: 'primary.main',
  color: 'white',
  fontSize: '0.75rem',
  py: 0.25,
  px: 1,
  minHeight: '28px',
  lineHeight: 1.2,
  '&:hover': {
    backgroundColor: 'primary.dark',
    color: 'white',
  },
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    '& > *:nth-of-type(1)': {
      fontSize: '16px',
    },
  },
};

/**
 * Generates sx for the filter toggle pill button.
 *
 * REUSABLE: Import this in pages with SubMenu to style the filter button consistently.
 */
export const filterToggleSx = (theme: Theme, expanded: boolean): SxProps<Theme> => ({
  fontSize: '0.75rem',
  py: 0.4,
  px: 1.5,
  minHeight: '30px',
  lineHeight: 1.2,
  borderRadius: '16px',
  textTransform: 'none',
  fontWeight: 500,
  ...(expanded
    ? {
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': { backgroundColor: 'primary.dark' },
      }
    : {
        borderColor: alpha(theme.palette.text.secondary, 0.3),
        color: 'text.secondary',
        '&:hover': {
          borderColor: 'primary.main',
          color: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
      }),
});

interface ListPageHeaderProps {
  /** Whether the filter panel is currently expanded */
  filtersExpanded: boolean;
  /** Callback to toggle filter panel visibility */
  onToggleFilters: () => void;
  /** Show the Create button */
  showCreate?: boolean;
  /** Callback when Create button is clicked */
  onCreateClick?: () => void;
  /** Show the Export button */
  showExport?: boolean;
  /** Callback when Export button is clicked */
  onExportClick?: () => void;
  /** Content rendered on the left side (e.g. help button) */
  leftContent?: React.ReactNode;
  /** Extra action buttons inserted between Filter and Create */
  extraActions?: React.ReactNode;
}

export const ListPageHeader: React.FC<ListPageHeaderProps> = ({
  filtersExpanded,
  onToggleFilters,
  showCreate,
  onCreateClick,
  showExport,
  onExportClick,
  leftContent,
  extraActions,
}) => {
  const translate = useTranslate();
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {leftContent}
      <Box sx={{ flex: 1 }} />

      {/* Filter toggle pill button */}
      <Button
        onClick={onToggleFilters}
        size="small"
        variant={filtersExpanded ? 'contained' : 'outlined'}
        startIcon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
        sx={filterToggleSx(theme, filtersExpanded)}
      >
        {translate('common.filters.label')}
      </Button>

      {extraActions}

      {showCreate && onCreateClick && (
        <Button
          onClick={onCreateClick}
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          sx={actionButtonSx}
        >
          {translate('common.create')}
        </Button>
      )}

      {showExport && onExportClick && (
        <Button
          onClick={onExportClick}
          size="small"
          variant="contained"
          startIcon={<ExportIcon />}
          sx={actionButtonSx}
        >
          {translate('common.export')}
        </Button>
      )}
    </Box>
  );
};

export default ListPageHeader;
